import { ROUTES } from '~/utils/constants';

/**
 * Normalizes permission strings from the API (e.g. "cn01 form_edit" vs "cn01_form_delete").
 */
export function normalizePermission(p: string): string {
  return p.trim().replace(/\s+/g, '_').toLowerCase();
}

export function buildPermissionSet(permissions: string[] | undefined): Set<string> {
  if (!permissions?.length) return new Set();
  return new Set(permissions.map(normalizePermission));
}

export function hasPermission(set: Set<string>, key: string): boolean {
  return set.has(normalizePermission(key));
}

export function hasAnyPermission(set: Set<string>, keys: readonly string[]): boolean {
  return keys.some((k) => hasPermission(set, k));
}

/** Any of these grants access to the CN02 manage/certificate route and sidebar entry. */
export const CN02_FORM_ACCESS_PERMISSIONS = [
  'cn02_form_view',
  'cn02_form_edit',
  'cn02_form_create',
  'cn02_form_delete',
] as const;

/** Any of these grants access to the CN03 form route and sidebar entry. */
export const CN03_FORM_ACCESS_PERMISSIONS = [
  'cn03_form_view',
  'cn03_form_edit',
  'cn03_form_create',
  'cn03_form_delete',
] as const;

/** Regional access to CN03 RG manage route and sidebar entry. */
export const CN03_RG_MANAGE_ACCESS_PERMISSIONS = ['cn03_form_view_rg'] as const;

/** CN03 form view for staff (TI) or regional (RG) manage users. */
export const CN03_FORM_VIEW_ACCESS_PERMISSIONS = [
  ...CN03_FORM_ACCESS_PERMISSIONS,
  ...CN03_RG_MANAGE_ACCESS_PERMISSIONS,
] as const;

/** Manage list route: RG users use the RG screen, others use the standard manage screen. */
export function resolveCn03ManagePath(permissionSet: Set<string>): string {
  if (hasPermission(permissionSet, 'cn03_form_view_rg')) {
    return ROUTES.CN03_MANAGE_RG;
  }
  return ROUTES.CN03_MANAGE;
}

/** For future /app/cn01-form staff route. */
export const CN01_FORM_ACCESS_PERMISSIONS = [
  'cn01_form_view',
  'cn01_form_edit',
  'cn01_form_create',
  'cn01_form_delete',
] as const;

/** Regional or Tea Inspector access to CN01 manage and view routes. */
export const CN01_MANAGE_ACCESS_PERMISSIONS = ['cn01_form_view_rg', 'cn01_form_view_ti'] as const;

/** CN04 manage list: Tea Inspector (own forms in region). */
export const CN04_FORM_VIEW_TI_PERMISSIONS = ['cn04_form_view_ti'] as const;

/** CN04 manage list: Regional (all inspectors in region). */
export const CN04_FORM_VIEW_RG_PERMISSIONS = ['cn04_form_view_rg'] as const;

/** Any of these grants access to the CN04 manage route and sidebar entry. */
export const CN04_MANAGE_ACCESS_PERMISSIONS = [
  ...CN04_FORM_VIEW_TI_PERMISSIONS,
  ...CN04_FORM_VIEW_RG_PERMISSIONS,
] as const;

export const CN04_FORM_CREATE_EDIT_PERMISSIONS = ['cn04_form_create', 'cn04_form_edit'] as const;

export const CN04_FORM_VIEW_PERMISSIONS = ['cn04_form_view'] as const;

export const CN04_FORM_DELETE_PERMISSIONS = ['cn04_form_delete'] as const;

/** CN04 form route (create, edit, view, or open from manage). */
export const CN04_FORM_ROUTE_PERMISSIONS = [
  ...CN04_FORM_CREATE_EDIT_PERMISSIONS,
  ...CN04_FORM_VIEW_PERMISSIONS,
  ...CN04_FORM_DELETE_PERMISSIONS,
  ...CN04_MANAGE_ACCESS_PERMISSIONS,
] as const;

export const CN04_FORM_CREATE_PERMISSIONS = ['cn04_form_create'] as const;

/** Sidebar / navigation: CN04 manage entry. */
export function resolveCn04ManagePath(_permissionSet: Set<string>): string {
  return ROUTES.CN04_MANAGE;
}

/** CN05 manage list: Tea Inspector (own forms in region). */
export const CN05_FORM_VIEW_TI_PERMISSIONS = ['cn05_form_view_ti'] as const;

/** CN05 manage list: Regional (all inspectors in region). */
export const CN05_FORM_VIEW_RG_PERMISSIONS = ['cn05_form_view_rg'] as const;

/** Any of these grants access to the CN05 manage route and sidebar entry. */
export const CN05_MANAGE_ACCESS_PERMISSIONS = [
  ...CN05_FORM_VIEW_TI_PERMISSIONS,
  ...CN05_FORM_VIEW_RG_PERMISSIONS,
] as const;

export const CN05_FORM_CREATE_EDIT_PERMISSIONS = ['cn05_form_create', 'cn05_form_edit'] as const;

export const CN05_FORM_VIEW_PERMISSIONS = ['cn05_form_view'] as const;

export const CN05_FORM_DELETE_PERMISSIONS = ['cn05_form_delete'] as const;

/** CN05 form route (create, edit, view, or open from manage). */
export const CN05_FORM_ROUTE_PERMISSIONS = [
  ...CN05_FORM_CREATE_EDIT_PERMISSIONS,
  ...CN05_FORM_VIEW_PERMISSIONS,
  ...CN05_FORM_DELETE_PERMISSIONS,
  ...CN05_MANAGE_ACCESS_PERMISSIONS,
] as const;

export const CN05_FORM_CREATE_PERMISSIONS = ['cn05_form_create'] as const;

/** Sidebar / navigation: CN05 manage entry. */
export function resolveCn05ManagePath(_permissionSet: Set<string>): string {
  return ROUTES.CN05_MANAGE;
}

/** CN06 manage list: Tea Inspector (own forms in region). */
export const CN06_FORM_VIEW_TI_PERMISSIONS = ['cn06_form_view_ti'] as const;

/** CN06 manage list: Regional (all inspectors in region). */
export const CN06_FORM_VIEW_RG_PERMISSIONS = ['cn06_form_view_rg'] as const;

/** Any of these grants access to the CN06 manage route and sidebar entry. */
export const CN06_MANAGE_ACCESS_PERMISSIONS = [
  ...CN06_FORM_VIEW_TI_PERMISSIONS,
  ...CN06_FORM_VIEW_RG_PERMISSIONS,
] as const;

export const CN06_FORM_CREATE_EDIT_PERMISSIONS = ['cn06_form_create', 'cn06_form_edit'] as const;

export const CN06_FORM_VIEW_PERMISSIONS = ['cn06_form_view'] as const;

export const CN06_FORM_DELETE_PERMISSIONS = ['cn06_form_delete'] as const;

/** CN06 form route (create, edit, view, or open from manage). */
export const CN06_FORM_ROUTE_PERMISSIONS = [
  ...CN06_FORM_CREATE_EDIT_PERMISSIONS,
  ...CN06_FORM_VIEW_PERMISSIONS,
  ...CN06_FORM_DELETE_PERMISSIONS,
  ...CN06_MANAGE_ACCESS_PERMISSIONS,
] as const;

export const CN06_FORM_CREATE_PERMISSIONS = ['cn06_form_create'] as const;

/** Sidebar / navigation: CN06 manage entry. */
export function resolveCn06ManagePath(_permissionSet: Set<string>): string {
  return ROUTES.CN06_MANAGE;
}

/** Planter / applicant CN01 status on dashboard. */
export const CN01_PLANTER_DASHBOARD_PERMISSIONS = ['cn01_form_view_pt'] as const;

/** Dashboard KPI analytics section. */
export const DASHBOARD_KEY_INDICATORS_PERMISSIONS = ['dashboard_key_indicators'] as const;

/** Dashboard regional office filter (all regions vs scoped). */
export const DASHBOARD_KEY_REGIONAL_PERMISSIONS = ['dashboard_regional'] as const;

/** Dashboard chart permissions. */
export const DASHBOARD_APPLICATION_STATUS_PERMISSIONS = ['dashboard_application_status'] as const;
export const DASHBOARD_YEAR_TREND_PERMISSIONS = ['dashboard_year_trend'] as const;
export const DASHBOARD_NURSERY_PERMISSIONS = ['dashboard_nursery'] as const;
export const DASHBOARD_APPLICATION_YEAR_PERMISSIONS = ['dashboard_application_year'] as const;
export const DASHBOARD_FORMS_PERMISSIONS = ['dashboard_forms'] as const;
export const DASHBOARD_INSPECTOR_PERMISSIONS = ['dashboard_inspector'] as const;

export const DASHBOARD_CHART_PERMISSIONS = [
  ...DASHBOARD_APPLICATION_STATUS_PERMISSIONS,
  ...DASHBOARD_YEAR_TREND_PERMISSIONS,
  ...DASHBOARD_NURSERY_PERMISSIONS,
  ...DASHBOARD_APPLICATION_YEAR_PERMISSIONS,
  ...DASHBOARD_FORMS_PERMISSIONS,
  ...DASHBOARD_INSPECTOR_PERMISSIONS,
] as const;

/** Any of these grants access to the analytics dashboard page. */
export const DASHBOARD_ANALYTICS_ACCESS_PERMISSIONS = [
  ...DASHBOARD_KEY_INDICATORS_PERMISSIONS,
  ...DASHBOARD_CHART_PERMISSIONS,
] as const;

/** Any of these grants access to Users module routes and sidebar entry. */
export const USERS_MODULE_ACCESS_PERMISSIONS = [
  'users_view',
  'users_create',
  'users_edit',
  'users_delete',
] as const;

/** Any of these grants access to Roles module routes and sidebar entry. */
export const ROLES_MODULE_ACCESS_PERMISSIONS = [
  'roles_view',
  'roles_create',
  'roles_edit',
  'roles_delete',
] as const;

/** Grants access to Reports module routes and sidebar entry. */
export const REPORTS_MODULE_ACCESS_PERMISSIONS = ['reports_module'] as const;

/** Any of these grants access to Suppliers manage routes and sidebar entry. */
export const SUPPLIER_MODULE_ACCESS_PERMISSIONS = [
  'supplier_view',
  'supplier_edit',
  'supplier_delete',
] as const;

/** Any of these grants access to Pest & Disease admin manage route and sidebar entry. */
export const PEST_DISEASE_MANAGE_ACCESS_PERMISSIONS = [
  'pest_disease_view',
  'pest_disease_create',
  'pest_disease_edit',
  'pest_disease_delete',
] as const;

/** Planter access to Pest & Disease browse page. */
export const PEST_DISEASE_PLANTER_ACCESS_PERMISSIONS = ['pest_disease_view_pt'] as const;

/** Any of these grants access to Financial manage routes and sidebar entry. */
export const FINANCIAL_MANAGE_ACCESS_PERMISSIONS = [
  'financial_view',
  'financial_create',
  'financial_edit',
  'financial_delete',
  'financial_calculate',
] as const;

/** Planter cost-estimation calculator. */
export const FINANCIAL_PLANTER_CALCULATE_PERMISSIONS = ['financial_calculate_pt'] as const;
